import { useEffect, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Key, Languages, MonitorSmartphone, Shield, Smartphone } from "lucide-react";

import { Button } from "@/components/ui/button";
import { useSession } from "../../application/hooks/useSession";
import { DataEncryptionControl } from "../components/DataEncryptionControl";
import { ChangePasswordDialog } from "../components/ChangePasswordDialog";
import { ChangeSignInMethodDialog } from "../components/ChangeSignInMethodDialog";
import {
  AuthenticatorDialog,
  type AuthenticatorDialogMode,
} from "../components/AuthenticatorDialog";
import { ManageDevicesDialog } from "@/features/sessions/presentation/components/ManageDevicesDialog";
import {
  DEFAULT_OTP_PREFERENCE,
  OTP_PREFERENCE_EMAIL,
  OTP_PREFERENCE_TOTP,
  OTP_PREFERENCE_LABELS,
  type OtpPreference,
} from "../../domain/constants/otpConstants";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useGetMeQuery } from "@/features/profile/infrastructure/api/ProfileApi";
import { useGetLanguagesQuery } from "@/features/languages/infrastructure/api/languagesApi";
import {
  useGetUserSettingsQuery,
  useUpdateUserSettingsMutation,
} from "@/features/user-settings/infrastructure/api/userSettingsApi";
import { LANGUAGE_STORAGE_KEY } from "@/core/i18n/config";
import { cn } from "@/lib/utils";
import { PageShell } from "@/components/layout/PageShell";

interface SettingRowProps {
  icon: ReactNode;
  title: string;
  description: string;
  action: ReactNode;
}

function SettingRow({ icon, title, description, action }: SettingRowProps) {
  return (
    <div className="flex flex-col gap-4 rounded-xl border border-gray-200 bg-white p-5 shadow-sm sm:flex-row sm:items-center sm:gap-6">
      <div className="flex h-10 w-10 shrink-0 items-center justify-center text-gray-500 [&_svg]:h-6 [&_svg]:w-6">
        {icon}
      </div>
      <div className="min-w-0 flex-1">
        <h2 className="text-base font-semibold text-gray-900">{title}</h2>
        <p className="mt-1 text-sm leading-relaxed text-gray-500">
          {description}
        </p>
      </div>
      <div className="shrink-0 sm:ml-auto">{action}</div>
    </div>
  );
}

const outlineActionClass =
  "min-w-[110px] cursor-pointer border-primary text-primary hover:bg-[#EAF2FF] hover:text-primary";

// Select triggers default to h-8; match the h-10 outline action buttons.
const selectActionClass = "cursor-pointer bg-white data-[size=default]:h-10";

const SettingsPage = () => {
  const { t, i18n } = useTranslation("settings");
  const { data: profile, refetch } = useGetMeQuery();
  const { getUserEmail, getIsTotpEnabled } = useSession();

  const { data: languages = [], isLoading: loadingLanguages } =
    useGetLanguagesQuery();
  const { data: userSettings } = useGetUserSettingsQuery();
  const [updateSettings, { isLoading: savingLanguage }] =
    useUpdateUserSettingsMutation();
  const [languageError, setLanguageError] = useState<string | null>(null);

  // Set Up / Reconfigure open the authenticator dialog in that mode.
  const [authenticatorDialogMode, setAuthenticatorDialogMode] =
    useState<AuthenticatorDialogMode | null>(null);
  const [devicesDialogOpen, setDevicesDialogOpen] = useState(false);
  const [changePasswordOpen, setChangePasswordOpen] = useState(false);
  // Method picked in the dropdown; the change dialog is open while it's set.
  const [pendingSignInMethod, setPendingSignInMethod] =
    useState<OtpPreference | null>(null);
  const [otpPreference, setOtpPreferenceState] = useState<OtpPreference>(
    DEFAULT_OTP_PREFERENCE,
  );

  useEffect(() => {
    if (profile?.otpPreference) {
      setOtpPreferenceState(profile.otpPreference);
    }
  }, [profile?.otpPreference]);

  const selectedLanguageId =
    userSettings?.webLanguage?.id ??
    languages.find((lang) => lang.code === i18n.language)?.id ??
    "";

  const handleLanguageChange = async (languageId: string) => {
    const language = languages.find((lang) => lang.id === languageId);
    if (!language) return;

    setLanguageError(null);
    void i18n.changeLanguage(language.code);
    localStorage.setItem(LANGUAGE_STORAGE_KEY, language.code);

    try {
      await updateSettings({ web_language: { id: language.id } }).unwrap();
    } catch {
      setLanguageError(t("language.updateError"));
    }
  };

  const totpEnabled = profile?.totpEnabled ?? getIsTotpEnabled() ?? false;

  // The dropdown keeps showing the saved method until the dialog succeeds.
  const handleOtpPreferenceChange = (preference: OtpPreference) => {
    if (preference === otpPreference) return;
    setPendingSignInMethod(preference);
  };

  const handleSignInMethodChanged = (method: OtpPreference) => {
    setOtpPreferenceState(method);
    void refetch();
  };

  const handleTotpAction = () => {
    setAuthenticatorDialogMode(totpEnabled ? "reconfigure" : "setup");
  };

  return (
    <PageShell>
      <h1 className="text-xl font-semibold text-gray-900">
        {t("pageTitle")}
      </h1>

      <div className="mx-auto mt-6 max-w-3xl space-y-4">
        <SettingRow
          icon={<Languages />}
          title={t("language.title")}
          description={t("language.description")}
          action={
            <div className="flex flex-col items-end gap-1">
              <Select
                value={selectedLanguageId}
                onValueChange={(value) => void handleLanguageChange(value)}
                disabled={loadingLanguages || savingLanguage}
              >
                <SelectTrigger
                  className={cn("w-[110px]", selectActionClass, outlineActionClass)}
                >
                  <SelectValue placeholder={t("language.placeholder")} />
                </SelectTrigger>
                <SelectContent>
                  {languages.map((lang) => (
                    <SelectItem key={lang.id} className="cursor-pointer" value={lang.id}>
                      {lang.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {languageError && (
                <p className="max-w-[220px] text-right text-xs text-red-600">
                  {languageError}
                </p>
              )}
            </div>
          }
        />

        <SettingRow
          icon={<Key />}
          title={t("password.title")}
          description={t("password.description")}
          action={
            <Button
              type="button"
              variant="outline"
              className={outlineActionClass}
              onClick={() => setChangePasswordOpen(true)}
            >
              {t("password.action")}
            </Button>
          }
        />

        <SettingRow
          icon={<Smartphone />}
          title={t("signInMethod.title")}
          description={t("signInMethod.description")}
          action={
            <div className="flex flex-col items-end gap-1">
              <Select
                value={otpPreference}
                onValueChange={(value) =>
                  handleOtpPreferenceChange(value as OtpPreference)
                }
              >
                <SelectTrigger
                  className={cn("w-[220px]", selectActionClass, outlineActionClass)}
                >
                  <SelectValue placeholder="Sign-in method" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem className="cursor-pointer" value={OTP_PREFERENCE_TOTP}>
                    {OTP_PREFERENCE_LABELS[OTP_PREFERENCE_TOTP]}
                  </SelectItem>
                  <SelectItem className="cursor-pointer" value={OTP_PREFERENCE_EMAIL}>
                    {OTP_PREFERENCE_LABELS[OTP_PREFERENCE_EMAIL]}
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
          }
        />

        <SettingRow
          icon={<Shield />}
          title={
            totpEnabled
              ? t("twoFactor.titleEnabled")
              : t("twoFactor.titleDisabled")
          }
          description={
            totpEnabled
              ? t("twoFactor.descriptionEnabled")
              : t("twoFactor.descriptionDisabled")
          }
          action={
            <Button
              type="button"
              variant="outline"
              className={outlineActionClass}
              onClick={handleTotpAction}
            >
              {totpEnabled
                ? t("twoFactor.actionReconfigure")
                : t("twoFactor.actionSetUp")}
            </Button>
          }
        />

        <SettingRow
          icon={<MonitorSmartphone />}
          title={t("devices.title")}
          description={t("devices.description")}
          action={
            <Button
              type="button"
              variant="outline"
              className={outlineActionClass}
              onClick={() => setDevicesDialogOpen(true)}
            >
              {t("devices.action")}
            </Button>
          }
        />

        <DataEncryptionControl />
      </div>

      <ManageDevicesDialog
        open={devicesDialogOpen}
        onOpenChange={setDevicesDialogOpen}
      />

      <ChangePasswordDialog
        open={changePasswordOpen}
        onOpenChange={setChangePasswordOpen}
        email={profile?.email ?? getUserEmail()}
        totpEnabled={totpEnabled}
        defaultMethod={otpPreference}
        onAuthenticatorEnabled={() => void refetch()}
      />

      <ChangeSignInMethodDialog
        open={pendingSignInMethod !== null}
        onOpenChange={(open) => {
          if (!open) setPendingSignInMethod(null);
        }}
        email={profile?.email ?? getUserEmail()}
        totpEnabled={totpEnabled}
        currentMethod={otpPreference}
        targetMethod={pendingSignInMethod}
        onAuthenticatorEnabled={() => void refetch()}
        onChanged={handleSignInMethodChanged}
      />

      <AuthenticatorDialog
        mode={authenticatorDialogMode ?? "setup"}
        open={authenticatorDialogMode !== null}
        onOpenChange={(open) => {
          if (!open) setAuthenticatorDialogMode(null);
        }}
        email={profile?.email ?? getUserEmail()}
        onAuthenticatorChanged={() => void refetch()}
      />
    </PageShell>
  );
};

export default SettingsPage;
